import {
  AdminInput,
  AdminSpinner,
  Button,
  Disclaimer,
  Div,
  EditPenIconRed,
  H5,
  Icon,
  Img,
  ModalPortal,
  P,
  Radio,
  getAllVerifiedLocations,
  isAdmin,
  lightTheme,
  restaurantImage,
  reportError,
} from '@vgrubs/components';
import { getPartnerLocations } from 'components/src/api/partner/locations.partner-api';
import {
  SimpleLocationEntity,
  SimpleLocationWithTodayDataResponse,
} from 'components/src/types/location.types';
import 'flatpickr/dist/flatpickr.css';
import debouce from 'lodash.debounce';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSearchParams } from 'react-router-dom';

export const AdminLocationPickerModal = ({
  // selectedLocation,
  // setSelectedLocation,
  isOpen,
  handleSubmit,
  customLocations = null,
  filterActive,
  onClose,
  changeParams,
}: {
  selectedLocation: SimpleLocationEntity | null;
  setSelectedLocation: Dispatch<SetStateAction<SimpleLocationEntity | null>>;
  isOpen: boolean;
  handleSubmit: (location: SimpleLocationEntity) => void;
  customLocations?: SimpleLocationEntity[] | null;
  filterActive?: boolean;
  onClose: () => void;
  changeParams: () => void;
}) => {
  const [searchParams] = useSearchParams();
  const locationName = searchParams.get('locationName');
  const [data, setData] = useState<SimpleLocationEntity[]>([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [ref, inView] = useInView();
  const [hasMore, setHasMore] = useState(true);
  const [isInit, setIsInit] = useState(true);
  const [selected, setSelected] = useState<SimpleLocationEntity | null>();
  const setSelectedLocation = setSelected;
  const selectedLocation = selected;

  async function getLocations(
    search: boolean,
    page: number,
    hasMore: boolean,
    query: string
  ) {
    if (customLocations && customLocations?.length > 0) {
      setData(customLocations);
      setSelectedLocation(customLocations[0]);
      return;
    }
    if (!hasMore) return;
    try {
      setLoading(true);

      const params = isAdmin
        ? {
            query,
            page,
            limit: 10,
          }
        : {
            query,
            page,
            limit: 10,
            verified: true,
          };

      const getFunction = isAdmin
        ? getAllVerifiedLocations
        : getPartnerLocations;

      const { data } = await getFunction(params);

      const dataLocations = isAdmin
        ? (data.data as SimpleLocationEntity[])
        : (data.data as SimpleLocationWithTodayDataResponse[]).map(
            ({ location }) => location
          );

      if (!search) {
        if (dataLocations[0]) {
          setHasMore(true);
          setData((prevItems) => [...prevItems, ...dataLocations]);
        } else {
          setHasMore(false);
        }
      } else {
        if (dataLocations[0]) {
          setHasMore(true);
          setData(() => [...dataLocations]);
        } else {
          setData(() => [...dataLocations]);
          setHasMore(false);
        }
      }

      if (dataLocations.length > 0 && isInit && selectedLocation === null) {
        const location = dataLocations[0];
        setSelectedLocation(location);
        setIsInit(false);
      }

      setPage((prevPage) => prevPage + 1);
    } catch (error) {
      setHasMore(false);
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (data.length < 1) {
      getLocations(false, page, hasMore, query);
    }
  }, []);

  useEffect(() => {
    if (inView && data.length > 0) {
      getLocations(false, page, hasMore, query);
    }
  }, [inView]);

  const handleSelectLocation = (newItem: SimpleLocationEntity) => {
    setSelectedLocation(newItem);
  };

  function isLocationSelected(item: SimpleLocationEntity) {
    return selectedLocation?.id === item.id;
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasMore(true);
    setPage(1);
    setQuery(e.target.value);
    getLocations(true, 1, true, e.target.value);
  };

  const debouncedResults = useMemo(() => {
    return debouce(handleChange, 500);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  return (
    <>
      {filterActive && locationName && (
        <Div $flex $alignCenter $gap="8px" $mb="16px" $mtSM="8">
          <P $m="0" style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}>
            <b>
              <span>{locationName}</span>
            </b>
            {/* |{' '} */}
            {/* <TextButton
            style={{
              color: lightTheme.primary,
            }}
            onClick={changeParams}
          >
            Change
          </TextButton> */}
          </P>{' '}
          <Icon $pointer $sizeSM="28" onClick={changeParams}>
            <EditPenIconRed />
          </Icon>
        </Div>
      )}
      <ModalPortal
        isOpen={isOpen}
        closeModal={onClose}
        modalOverflow="hidden"
        header="Select Location"
        overlayBorderRadius={30}
        logo={false}
        footer={
          <Button
            $selected
            $w="100%"
            $wSM="100%"
            onClick={() => {
              // setModalPortalIsOpen((prev) => !prev);
              if (selected) {
                handleSubmit(selected);
              }
            }}
          >
            Confirm
          </Button>
        }
      >
        <Div $relative>
          <Div $p="20px">
            <AdminInput
              search
              onChange={debouncedResults}
              placeholder="Search Location"
            />
          </Div>
          <Div $minHeight="50vh">
            {data.length === 0 && !hasMore && (
              <Div
                $flex
                $center
                $column
                $height="calc(70vh - 300px)"
                $heightSM="calc(60vh - 300px)"
              >
                <Img
                  src={restaurantImage}
                  alt="restaurant"
                  $width="250px"
                  $widthSM="150px"
                />
                <H5>No Locations</H5>
              </Div>
            )}
            {data.map((lc, i) => {
              const l = lc as SimpleLocationEntity;
              return (
                <Div
                  key={i}
                  $box
                  $p="14px 24px"
                  $mb="8"
                  $flex
                  $alignCenter
                  $between
                  onClick={() => handleSelectLocation(l)}
                  $gap="16px"
                  $pointer
                  // delay={0.1}
                >
                  <div>
                    <P
                      $left
                      $bold
                      $mb="8"
                      $color={isLocationSelected(l) && lightTheme.primary}
                      $lineHeight="1.2"
                    >
                      {l.name}
                    </P>
                    <Disclaimer $left $mb="0">
                      {l.address}
                    </Disclaimer>
                  </div>

                  <Radio checked={isLocationSelected(l)} />
                </Div>
              );
            })}
            <div ref={ref}></div>
            <Div $height="70px" $heightSM="70px" $relative $mb="-30">
              {loading && (
                <AdminSpinner absolute small $noBackground $noZIndex />
              )}
            </Div>
          </Div>
        </Div>
      </ModalPortal>
    </>
  );
};
